import Image from "next/image";
import Link from "next/link";
import { getGlobalData } from "utils/api";
import { CMSUrls } from "utils/cmsHelper";
import { getLocalizedPaths } from "utils/localize";

import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function IssueWithTransaction({ metadata }) {
  const transactionItems = [
    {
      title: "Is the transaction on your account?",
      description: "Check your account via our app.",
      iconUrl: "/images/icons/profile.svg",
      iconAlt: "account icon",
    },
    {
      title: "Do you recognise the transaction or amount?",
      description: "Check receipts, it's easy to confuse similar amounts.",
      iconUrl: "/images/icons/page.svg",
      iconAlt: "transaction icon",
    },
    {
      title: "Can you call the retailer / merchant?",
      description:
        "Most retailers/merchants are happy to help if a mistake has been made. You may find their contact details on your receipt or the internet.",
      iconUrl: "/images/icons/mobile-call.svg",
      iconAlt: "call retailer icon",
    },
    {
      title: "Have you been a victim of a scam?",
      description:
        "If you believe you've been a victim of a scam please let us know.",
      iconUrl: "/images/icons/scam-alert.svg",
      iconAlt: "scam icon",
    },
  ];

  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-12 lg:gap-spacing-14 text-sm lg:text-base">
        <Breadcrumb />
        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/circle-right.svg"
            alt="right-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              First and Foremost, Stay Calm
            </h3>
            <p className="4xl:text-xl">
              In many instances, transactions are legitimate, and people may
              simply forget about them. We recommend taking a moment to
              double-check and confirm whether it&apos;s something you recognise
              and have authorised.
            </p>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-6 text-center">
          {transactionItems.map((item, index) => {
            return (
              <div
                className="w-[328px] 4xl:h-[260px] lg:w-[448px] h-[282px] lg:h-[234px] bg-onmoPurple-50 rounded-xl  px-4 md:px-6  py-10 pb-6 lg:py-6 flex flex-col text-center justify-self-center"
                key={index}
              >
                <div className="flex flex-col items-center justify-center">
                  <Image
                    src={item.iconUrl}
                    width={32}
                    height={32}
                    alt={item.iconAlt}
                    className="h-[32px] w-[32px]"
                  />
                  <div className="flex flex-col mt-5">
                    <h4 className="text-onmoText font-medium text-xl 4xl:text-2xl mb-4">
                      {item.title}
                    </h4>
                    <p className="text-onmoText 4xl:text-lg">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/raise-dispute.svg"
            alt="raise-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Raising a dispute
            </h3>
            <p className="4xl:text-xl">
              If you believe there is an issue with a transaction, please fill
              out the following disputes{" "}
              <Link
                className="text-onmoPrimary underline"
                href={CMSUrls.transactionDisputeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                form{" "}
              </Link>{" "}
              {"  "}so we can investigate it for you.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/more-info.svg"
            alt="info-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              Important:
            </h3>
            <ul className="list-disc pl-6 4xl:text-xl">
              <li>
                Make sure to contact the company that charged you first to see
                if they can assist. If they are unable to help, complete all
                five sections of the form and include any supporting documents
                that might help us understand the situation.
              </li>
              <li>
                Send the completed form by attaching it to an email and sending
                it to{" "}
                <Link href="mailto:fraudSupport@onmo.app">
                  FraudSupport@onmo.app
                </Link>{" "}
                . Please avoid using Adobe&lsquo;s file-sharing option, as we
                will not receive it. Alternatively, you can mail the form to us.
                You can download the form{" "}
                <Link
                  className="text-onmoPrimary underline"
                  href={CMSUrls.transactionDisputeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </Link>
                .
              </li>
            </ul>
          </div>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Issue with a Transaction`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
